import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { StickerImage } from "@/components/StickerImage";

const PACK_IMG = "/pack/pacchetto.png";
// Fuochi d'artificio a schermo pieno: centri di esplosione sparsi sullo schermo.
const BURSTS = [
  { x: 25, y: 28, c: "#ffd166", d: 0 },
  { x: 72, y: 22, c: "#ef476f", d: 0.2 },
  { x: 50, y: 16, c: "#06d6a0", d: 0.45 },
  { x: 32, y: 62, c: "#118ab2", d: 0.7 },
  { x: 80, y: 58, c: "#f78c6b", d: 0.95 },
  { x: 55, y: 46, c: "#ffd166", d: 1.2 },
  { x: 14, y: 50, c: "#ef476f", d: 0.35 },
  { x: 88, y: 38, c: "#06d6a0", d: 0.85 },
];
const PARTICLES = 12;
const RADIUS = 100;
const pad = (n) => String(n).padStart(3, "0");

// Animazione di apertura del pacchetto gratuito (tempi/ordine esatti).
// Le figurine (001,002,051,052) sono GIÀ salvate sul server prima di montare l'overlay.
export const PackOpening = ({ stickers, onComplete }) => {
  // fasi: closed (1s) -> unwrap (2s) -> fireworks (2s) -> reveal -> done
  const [phase, setPhase] = useState("closed");
  const [revealed, setRevealed] = useState(0);
  const timersRef = useRef([]);
  const audioRef = useRef({});

  useEffect(() => {
    const A = (src) => {
      const a = new Audio(src);
      a.preload = "auto";
      return a;
    };
    audioRef.current = {
      scarto: A("/pack/scarto.mp3"),
      fuochi: A("/pack/fuochi.mp3"),
      musica: A("/pack/musica.mp3"),
      versi: A("/pack/versi.mp3"),
    };
    const play = (k) => {
      const a = audioRef.current[k];
      if (!a) return;
      try {
        a.currentTime = 0;
        a.play().catch(() => {});
      } catch {
        /* audio decorativo, mai bloccante */
      }
    };
    const stop = (k) => {
      const a = audioRef.current[k];
      if (a) {
        try {
          a.pause();
        } catch {
          /* no-op */
        }
      }
    };
    const at = (ms, fn) => timersRef.current.push(setTimeout(fn, ms));

    // 1) pacchetto chiuso 1s
    // 2) si muove e si scarta: audio scarto (2s)
    at(1000, () => {
      setPhase("unwrap");
      play("scarto");
    });
    // 3) finito lo scarto: fuochi 2s + audio fuochi
    at(3000, () => {
      stop("scarto");
      setPhase("fireworks");
      play("fuochi");
    });
    // 4) solo a fuochi terminati: pacchetto aperto -> musica + versi insieme, inizia 1a figurina
    at(5000, () => {
      stop("fuochi");
      setPhase("reveal");
      play("musica");
      play("versi");
      setRevealed(1);
    });
    at(5850, () => setRevealed(2));
    at(6700, () => setRevealed(3));
    at(7550, () => setRevealed(4));
    // 5) musica ininterrotta fino alla fine dell'apertura, poi stop
    at(8600, () => {
      stop("musica");
      setPhase("done");
    });

    return () => {
      timersRef.current.forEach(clearTimeout);
      Object.values(audioRef.current).forEach((a) => {
        try {
          a.pause();
        } catch {
          /* no-op */
        }
      });
    };
  }, []);

  // Salta: ferma audio/timer e va all'album. Le figurine restano registrate (salvataggio server).
  const skip = () => {
    timersRef.current.forEach(clearTimeout);
    Object.values(audioRef.current).forEach((a) => {
      try {
        a.pause();
      } catch {
        /* no-op */
      }
    });
    onComplete();
  };

  const opened = phase === "reveal" || phase === "done";
  const done = phase === "done";
  const showSkip = opened && !done;

  return (
    <motion.div
      data-testid="pack-opening-overlay"
      className="fixed inset-0 z-50 bg-[#2b2622]/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <h2 className="text-white text-xl font-extrabold mb-6">
        {done ? "Pacchetto aperto!" : opened ? "Escono le figurine…" : "Apertura pacchetto…"}
      </h2>

      {/* Pacchetto chiuso / scarto */}
      {!opened && (
        <div className="relative flex items-center justify-center" style={{ width: 220, height: 320 }}>
          <motion.img
            src={PACK_IMG}
            alt="Pacchetto AMOCANIAMOGATTI"
            data-testid="pack-closed-img"
            className="w-[200px] h-[300px] object-contain drop-shadow-2xl rounded-2xl"
            animate={
              phase === "unwrap"
                ? { rotate: [-3, 3, -3, 3, 0], y: [0, -6, 4, -4, 0], scale: [1, 1.04, 1.02, 1.05, 1] }
                : {}
            }
            transition={phase === "unwrap" ? { duration: 2, ease: "easeInOut" } : {}}
          />
        </div>
      )}

      {/* Fuochi d'artificio a schermo pieno (2s) */}
      {phase === "fireworks" && (
        <div data-testid="pack-fireworks" className="fixed inset-0 z-[60] pointer-events-none">
          {BURSTS.map((b, bi) => (
            <div key={bi} className="absolute" style={{ left: `${b.x}%`, top: `${b.y}%` }}>
              <motion.span
                className="absolute rounded-full -translate-x-1/2 -translate-y-1/2"
                style={{ width: 16, height: 16, background: b.c, boxShadow: `0 0 28px ${b.c}` }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: [0, 2.2, 0], opacity: [0, 1, 0] }}
                transition={{ duration: 0.9, delay: b.d, repeat: 1, repeatDelay: 0.1 }}
              />
              {Array.from({ length: PARTICLES }).map((_, pi) => {
                const ang = (pi / PARTICLES) * Math.PI * 2;
                const dx = Math.cos(ang) * RADIUS;
                const dy = Math.sin(ang) * RADIUS;
                return (
                  <motion.span
                    key={pi}
                    className="absolute rounded-full"
                    style={{ width: 9, height: 9, background: b.c, boxShadow: `0 0 14px ${b.c}` }}
                    initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
                    animate={{ x: dx, y: dy, opacity: [0, 1, 0], scale: [0.4, 1.2, 0.3] }}
                    transition={{ duration: 1.1, delay: b.d, repeat: 1, repeatDelay: 0.1 }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Apertura: 4 figurine una alla volta con movimento visibile */}
      {opened && (
        <div className="grid grid-cols-2 gap-4 max-w-xs w-full">
          {stickers.map((s, i) => (
            <div key={s.number} className="aspect-[3/4]">
              <AnimatePresence>
                {i < revealed && (
                  <motion.div
                    data-testid={`pack-card-${pad(s.number)}`}
                    initial={{ y: -130, scale: 0.4, rotate: -12, opacity: 0 }}
                    animate={{ y: 0, scale: 1, rotate: 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 170, damping: 14 }}
                    className="w-full h-full bg-white border-4 border-[#e8836b] rounded-2xl overflow-hidden flex items-center justify-center shadow-xl"
                  >
                    <StickerImage number={s.number} className="w-full h-full object-contain p-1" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 text-center min-h-[52px]">
        {done && (
          <>
            <p data-testid="pack-result" className="text-white font-extrabold text-lg mb-4">
              {stickers.length}/{stickers.length} figurine ottenute!
            </p>
            <Button
              data-testid="pack-done-btn"
              onClick={onComplete}
              className="bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full px-8 py-6 text-sm font-bold"
            >
              VAI ALL’ALBUM
            </Button>
          </>
        )}
      </div>

      {showSkip && (
        <button
          data-testid="pack-skip-btn"
          onClick={skip}
          className="absolute bottom-6 text-white/70 hover:text-white text-sm underline"
        >
          Salta animazione
        </button>
      )}
    </motion.div>
  );
};
